/**
 * ChartRenderer — top-level chart wrapper.
 *
 * Renders a chart title, accessible description, the appropriate chart
 * sub-component (selected by spec.type), and action buttons (fullscreen +
 * download dropdown). Handles the fullscreen modal state internally.
 */

import { useState, useRef } from "react";
import type {
	ChartSpec,
	BarChartConfig,
	PieChartConfig,
	LineChartConfig,
	GanttChartConfig,
	WordCloudConfig,
	TableChartConfig,
	WorkflowChartConfig,
	KanbanChartConfig,
} from "../../types/index.js";
import BarChartView from "./BarChartView.js";
import PieChartView from "./PieChartView.js";
import LineChartView from "./LineChartView.js";
import GanttChartView from "./GanttChartView.js";
import WordCloudView from "./WordCloudView.js";
import TableView from "./TableView.js";
import WorkflowChartView from "./WorkflowChartView.js";
import KanbanBoardView from "./KanbanBoardView.js";
import ChartModal from "./ChartModal.js";
import { exportSvgAsPng, exportSvgAsSvg, findSvgInContainer, copyTableAsText, copyKanbanToClipboard } from "./chartUtils.js";

interface ChartRendererProps {
	spec: ChartSpec;
	isModal?: boolean;
}

export default function ChartRenderer({ spec, isModal = false }: ChartRendererProps) {
	const [showModal, setShowModal] = useState(false);
	const [downloadOpen, setDownloadOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const chartContainerRef = useRef<HTMLDivElement>(null);

	const isTable = spec.type === "table";
	const isKanban = spec.type === "kanban";
	const isCopyable = isTable || isKanban;
	const copyLabel = isKanban ? "Copy board to clipboard" : "Copy table to clipboard";

	const handleCopy = async () => {
		let success: boolean;
		if (isKanban) {
			success = await copyKanbanToClipboard(spec.config as KanbanChartConfig);
		} else {
			success = await copyTableAsText(spec.config as TableChartConfig);
		}
		if (success) {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleDownloadPng = async () => {
		setDownloadOpen(false);
		if (!chartContainerRef.current) return;
		const svgEl = findSvgInContainer(chartContainerRef.current);
		if (svgEl) {
			await exportSvgAsPng(svgEl, `${spec.title.replace(/\s+/g, "-").toLowerCase()}.png`);
		}
	};

	const handleDownloadSvg = () => {
		setDownloadOpen(false);
		if (!chartContainerRef.current) return;
		const svgEl = findSvgInContainer(chartContainerRef.current);
		if (svgEl) {
			exportSvgAsSvg(svgEl, `${spec.title.replace(/\s+/g, "-").toLowerCase()}.svg`);
		}
	};

	const renderChart = () => {
		switch (spec.type) {
			case "bar":
				return <BarChartView config={spec.config as BarChartConfig} isModal={isModal} />;
			case "pie":
				return <PieChartView config={spec.config as PieChartConfig} isModal={isModal} />;
			case "line":
				return <LineChartView config={spec.config as LineChartConfig} isModal={isModal} />;
			case "gantt":
				return <GanttChartView config={spec.config as GanttChartConfig} isModal={isModal} />;
			case "wordcloud":
				return <WordCloudView config={spec.config as WordCloudConfig} isModal={isModal} />;
			case "table":
				return <TableView config={spec.config as TableChartConfig} isModal={isModal} />;
			case "workflow":
				return <WorkflowChartView config={spec.config as WorkflowChartConfig} isModal={isModal} />;
			case "kanban":
				return <KanbanBoardView config={spec.config as KanbanChartConfig} isModal={isModal} />;
			default:
				return (
					<div className="chart-empty-state" role="status">
						Unsupported chart type: {spec.type}
					</div>
				);
		}
	};

	return (
		<div className="chart-container">
			{/* Screen-reader-only accessible description */}
			<span className="sr-only">{spec.description}</span>

			{/* Chart header */}
			<div className="chart-header">
				<h3 className="chart-title">{spec.title}</h3>

				<div className="chart-actions">
					{/* Fullscreen button */}
					<button
						className="chart-action-btn chart-action-btn--icon"
						onClick={() => setShowModal(true)}
						aria-label={`View ${spec.title} in fullscreen`}
						title="View fullscreen"
					>
						<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="13" height="13" aria-hidden="true">
							<path d="M1 5V1h4M9 1h4v4M13 9v4H9M5 13H1V9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</button>

					{/* Table/Kanban: copy to clipboard; other charts: download dropdown */}
					{isCopyable ? (
						<button
							className={`chart-action-btn chart-copy-btn${copied ? " chart-copy-btn--copied" : ""}`}
							onClick={handleCopy}
							aria-label={copied ? "Copied to clipboard" : copyLabel}
							title={copied ? "Copied!" : "Copy to clipboard"}
						>
							{copied ? (
								<>
									<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">
										<path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
									Copied!
								</>
							) : (
								<>
									<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">
										<rect x="5" y="1" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
										<path d="M9 10v2a1 1 0 01-1 1H2a1 1 0 01-1-1V5a1 1 0 011-1h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
									</svg>
									Copy
								</>
							)}
						</button>
					) : (
						/* Download dropdown */
						<div style={{ position: "relative" }}>
							<button
								className="chart-action-btn chart-action-btn--icon"
								onClick={() => setDownloadOpen((o) => !o)}
								aria-label="Download chart"
								aria-expanded={downloadOpen}
								aria-haspopup="true"
								title="Download"
							>
								<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="13" height="13" aria-hidden="true">
									<path d="M7 1v8M4 6.5L7 10l3-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									<path d="M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
								</svg>
							</button>

							{downloadOpen && (
								<>
									<div
										style={{ position: "fixed", inset: 0, zIndex: 10 }}
										onClick={() => setDownloadOpen(false)}
										aria-hidden="true"
									/>
									<div className="chart-download-dropdown" role="menu">
										<button
											className="chart-download-option"
											role="menuitem"
											onClick={handleDownloadPng}
										>
											Download as PNG
										</button>
										<button
											className="chart-download-option"
											role="menuitem"
											onClick={handleDownloadSvg}
										>
											Download as SVG
										</button>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Chart body */}
			<div
				ref={chartContainerRef}
				className="chart-body"
				aria-label={spec.description}
			>
				{renderChart()}
			</div>

			{/* Fullscreen modal */}
			{showModal && (
				<ChartModal
					spec={spec}
					onClose={() => setShowModal(false)}
				/>
			)}
		</div>
	);
}
